// Map CSV columns to system table format - simplified since columns are now standardized
export const mapCsvColumnsToSystemTable = (data: any[], tableType: 'inventory' | 'sales'): any[] => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Process each row, maintaining the same structure but ensuring required fields
  const mappedData = data.map(row => {
    // Add Source field if not present
    if (!row.Source) {
      row.Source = 'CSV Import';
    }
    return row;
  });

  return mappedData;
};

// Improved CSV parsing function with better header detection and description row removal
export const parseCSV = (csvContent: string): { data: any[], metadata: { description: string, headerRowIndex: number } } => {
  if (!csvContent || csvContent.trim() === '') {
    console.error('CSV content is empty');
    return { data: [], metadata: { description: '', headerRowIndex: 0 } };
  }

  // Normalize line endings and remove trailing empty lines
  const rawLines = csvContent.split(/\r?\n/);
  const lines = rawLines.map(l => l.replace(/\r$/, '')).filter((l, idx, arr) => {
    // Keep all non-empty lines and keep empty lines in the middle,
    // but drop trailing empty lines at the end of the file
    if (l.trim() !== '') return true;
    return idx < arr.length - 1;
  });

  if (lines.length === 0) {
    console.error('No lines found in CSV content');
    return { data: [], metadata: { description: '', headerRowIndex: 0 } };
  }

  /**
   * Header detection
   *
   * The tests expect a very simple, predictable behaviour:
   * - Treat everything before the first line containing a comma as metadata/description
   * - Use the first line that contains a comma as the header row
   *
   * This works well for:
   * - Simple CSVs with standard headers (first line is the header)
   * - Amazon-style reports where the first few lines are metadata, followed by a header row
   */
  let headerRowIndex = -1;
  let description = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.includes(',')) {
      headerRowIndex = i;
      break;
    }
  }

  // Fallback: if no comma found at all, treat the first line as header
  if (headerRowIndex === -1) {
    headerRowIndex = 0;
  }

  // Everything before the header row is considered description/metadata
  if (headerRowIndex > 0) {
    description = lines.slice(0, headerRowIndex).join('\n').trim();
  }

  const result = [];

  // Get headers from the detected header row and sanitize them for SQL safe column names
  const headers = lines[headerRowIndex].split(',').map(header => {
    // Clean and normalize the header
    const cleanHeader = header.trim().replace(/"/g, '');

    // Create a SQL-safe column name
    // Replace spaces, special chars with underscores and ensure it doesn't start with a number
    return cleanHeader.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase();
  });

  // Process data rows after the header row
  for (let i = headerRowIndex + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) {
      continue;
    }

    // Parse the CSV line properly, handling quoted values and commas within quotes
    const values = parseCSVLine(line);

    // If we don't have the right number of values, adjust the array
    const normalizedValues = normalizeValues(values, headers.length);

    const entry: Record<string, string> = {};

    for (let j = 0; j < headers.length; j++) {
      if (headers[j]) {
        // Always store as string to avoid numeric parsing issues
        entry[headers[j]] = String(normalizedValues[j] || '');
      }
    }

    if (Object.keys(entry).length > 0) {
      result.push(entry);
    }
  }

  return {
    data: result,
    metadata: {
      description,
      headerRowIndex
    }
  };
};

// Helper function to parse a CSV line correctly, handling quotes
export const parseCSVLine = (line: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let inQuotes = false;

  for (let j = 0; j < line.length; j++) {
    const char = line[j];

    if (char === '"' && (j === 0 || line[j - 1] !== '\\')) {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      values.push(currentValue.trim().replace(/^"|"$/g, ''));
      currentValue = '';
    } else {
      currentValue += char;
    }
  }

  // Add the last value
  values.push(currentValue.trim().replace(/^"|"$/g, ''));

  // If parsing failed (e.g., quotes not matched properly), fall back to simple split
  if (values.some(v => v.includes('"') && !v.startsWith('"') && !v.endsWith('"'))) {
    return line.split(',').map(value => value.trim().replace(/^"|"$/g, ''));
  }

  return values;
};

// Helper function to normalize array length
const normalizeValues = (values: string[], expectedLength: number): string[] => {
  const result = [...values];

  // Add empty strings for missing values
  while (result.length < expectedLength) {
    result.push('');
  }

  // Remove extra values
  while (result.length > expectedLength) {
    result.pop();
  }

  return result;
};
